import { RESOURCES_STRINGS } from './constants/resources-strings';

/**
 * What activating a resource card does.
 *
 * `open_external` — the item is a link; the card navigates straight to it.
 * `view_details`  — the item is file-backed; the card opens the detail dialog.
 */
export type ResourceAction = 'open_external' | 'view_details';

/** The card's call-to-action copy, keyed by the resolved action. */
export const RESOURCE_ACTION_LABEL: Record<ResourceAction, string> = {
  open_external: RESOURCES_STRINGS.OPEN_RESOURCE,
  view_details: RESOURCES_STRINGS.VIEW_DETAILS,
};

/** The subset of a resource item that decides its action. */
export interface ResourceActionSource {
  externalUrl: string | null;
  fileUrl: string | null;
}

/**
 * Resolve what a resource card should do when activated.
 *
 * An external link always wins: an item carrying one is a pointer to somewhere
 * else, so there is nothing to preview. Everything else — PDF, image, or any
 * other uploaded file — opens the detail dialog. Items with neither a link nor
 * a file also resolve to `view_details`, matching the pre-existing open
 * behaviour, so a half-populated record never renders a dead external link.
 *
 * Resolved once per item and read for both the label and the click handler, so
 * the two can never disagree about what kind of item this is.
 *
 * @param item - the item's link and file fields
 * @returns the action this item's card performs
 */
export function resolveResourceAction(item: ResourceActionSource): ResourceAction {
  return item.externalUrl?.trim() ? 'open_external' : 'view_details';
}
